import * as amplitude from "@amplitude/analytics-browser"
import { Identify } from "@amplitude/analytics-browser"

class AnalyticsService {
  private static instance: AnalyticsService
  private isInitialized = false

  private constructor() {
    // Private constructor for singleton
  }

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService()
    }
    return AnalyticsService.instance
  }

  public init(): void {
    const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY
    if (!apiKey) {
      console.error("Amplitude API Key not found. Analytics will be disabled.")
      return
    }

    if (this.isInitialized) {
      console.warn("Amplitude is already initialized.")
      return
    }

    try {
      amplitude.init(apiKey, undefined, {
        // Disable automatic tracking (page views, sessions, etc.)
        autocapture: {
          sessions:true,
          pageViews:true,
          formInteractions:false,
          fileDownloads:false,
          elementInteractions:false,
          attribution:false,
        },
        trackingOptions: {
          ipAddress: true,
        },
      })
      this.isInitialized = true
      console.log("Amplitude initialized successfully.")
    } catch (error) {
      console.error("Failed to initialize Amplitude:", error)
      this.isInitialized = false
    }
  }

  public identifyUser({userId, userProperties}: {userId?: string, userProperties?: Record<string, string>}): void {
    if (!this.isInitialized) {
      console.warn(
        "Amplitude not initialized. Call init() before identifying user."
      )
      return
    }

    try {
      if(userId) {
        amplitude.setUserId(userId)
        console.log(`Amplitude user ID set to: ${userId}`)
      }

      if (userProperties && Object.keys(userProperties).length > 0) {
        const identify = new Identify()
        for (const key in userProperties) {
          if (Object.prototype.hasOwnProperty.call(userProperties, key)) {
            // Use identify.set to add or update properties.
            // Other operations like setOnce, add, unset etc. are available
            identify.set(key, userProperties[key])
          }
        }
        amplitude.identify(identify)
        console.log("Amplitude user properties identified:", userProperties)
      }
    } catch (error) {
      console.error("Failed to identify Amplitude user:", error)
    }
  }

  public trackEvent(
    eventName: string,
    eventProperties?: Record<string, any>
  ): void {
    if (!this.isInitialized) {
      console.warn(
        `Amplitude not initialized. Event "${eventName}" not tracked.`
      )
      return
    }

    try {
      amplitude.track(eventName, eventProperties)
      console.log(`Amplitude event tracked: ${eventName}`, eventProperties || {})
    } catch (error) {
      console.error(`Failed to track Amplitude event "${eventName}":`, error)
    }
  }

  public clearUser(): void {
    if (!this.isInitialized) {
      console.warn("Amplitude not initialized. Cannot clear user.")
      return
    }

    try {
      // Resets userId to undefined and generates a new deviceId
      amplitude.reset()
      console.log("Amplitude user reset (logout).")
    } catch (error) {
      console.error("Failed to reset Amplitude user:", error)
    }
  }
}

// Export the singleton instance
export const analyticsService = AnalyticsService.getInstance() 